const LEAVE_STATUS_META = {
  approved: { label: "Approved", tone: "success" },
  pending: { label: "Pending", tone: "warning" },
  rejected: { label: "Rejected", tone: "danger" },
  "": { label: "—", tone: "muted" },
};

export const LEAVE_TYPE_META = {
  casual_leave: {
    label: "Casual Leave",
    accent: "sky",
    icon: "plane",
  },
  sick_leave: {
    label: "Sick Leave",
    accent: "amber",
    icon: "shield",
  },
  privilege_leave: {
    label: "Privilege Leave",
    accent: "blue",
    icon: "userCircle",
  },
  maternity_leave: {
    label: "Maternity Leave",
    accent: "pink",
    icon: "users",
  },
  comp_off: {
    label: "Comp Off",
    accent: "green",
    icon: "briefcase",
  },
  other: {
    label: "Others",
    accent: "muted",
    icon: "calendar",
  },
};

export const EMPLOYEE_LEAVE_DURATION_OPTIONS = [
  { value: "full_day", label: "Full Day" },
  { value: "half_day_am", label: "Half Day (AM)" },
  { value: "half_day_pm", label: "Half Day (PM)" },
  { value: "multiple_days", label: "Multiple Days" },
];

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function getCurrentMonthKey() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export function shiftMonthKey(monthKey, step) {
  const [year, month] = String(monthKey || getCurrentMonthKey())
    .split("-")
    .map((value) => Number(value));

  const date = new Date(year, month - 1 + Number(step || 0), 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export function formatMonthHeading(monthKey) {
  const [year, month] = String(monthKey || getCurrentMonthKey())
    .split("-")
    .map((value) => Number(value));

  return new Intl.DateTimeFormat("en-IN", {
    month: "long",
    year: "numeric",
  }).format(new Date(year, month - 1, 1));
}

export function getLeaveStatusMeta(status) {
  return LEAVE_STATUS_META[status] || LEAVE_STATUS_META[""];
}

export function getLeaveTypeMeta(leaveType) {
  return LEAVE_TYPE_META[leaveType] || LEAVE_TYPE_META.other;
}

export function formatLeaveDaysValue(value) {
  const amount = Number(value || 0);
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(1);
}

export function formatLeaveDateLabel(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function formatLeaveDayLabel(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    weekday: "short",
  }).format(date);
}

export function buildEmployeeLeavesCalendar(monthKey, entries) {
  const entryMap = new Map((entries || []).map((entry) => [entry.dateKey, entry]));
  const [year, month] = String(monthKey || getCurrentMonthKey())
    .split("-")
    .map((value) => Number(value));

  const firstDate = new Date(year, month - 1, 1);
  const lastDate = new Date(year, month, 0);
  const leadingDays = firstDate.getDay();
  const totalDays = lastDate.getDate();
  const cells = [];

  for (let index = 0; index < leadingDays; index += 1) {
    cells.push({
      key: `empty-start-${index}`,
      isPlaceholder: true,
    });
  }

  for (let day = 1; day <= totalDays; day += 1) {
    const dateKey = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const entry = entryMap.get(dateKey);

    cells.push({
      key: dateKey,
      isPlaceholder: false,
      dateKey,
      day,
      status: entry?.status || "",
      requestCount: Number(entry?.requestCount || 0),
      leaveType: entry?.leaveType || "",
      meta: getLeaveStatusMeta(entry?.status || ""),
      typeMeta: getLeaveTypeMeta(entry?.leaveType || ""),
    });
  }

  while (cells.length % 7 !== 0) {
    cells.push({
      key: `empty-end-${cells.length}`,
      isPlaceholder: true,
    });
  }

  return {
    weekdays: WEEKDAY_LABELS,
    cells,
  };
}

export function downloadLeaveCsv(fileName, rows) {
  const csv = rows
    .map((row) =>
      row
        .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
        .join(",")
    )
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
